import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getFIs } from 'src/financial-institution';
import { getToken } from 'src/auth';
import { InnerLoader } from 'src/ui';
import { api } from 'src/api/config';
import CommissionPayoutModal from '../components/CommissionPayoutModal';

/**
 * FI Dashboard Page
 * Displays metrics and merchant list for a specific Financial Institution
 */
const FIDashboardPage = ({ financialInstitutions, token }) => {
  const { id: fiId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [commissionsModalOpen, setCommissionsModalOpen] = useState(false);

  // Get FI from list
  const financialInstitution = financialInstitutions.items?.find(
    (fi) => fi.id === parseInt(fiId)
  );

  // Fetch FI metrics and merchant list
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if token is available
        if (!token || !token.accessToken) {
          console.error('Token not available:', token);
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Get date range (default: last 30 days)
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split('T')[0];

        console.log('Fetching FI Dashboard data for FI:', fiId);
        console.log('Token available:', !!token.accessToken);
        console.log('Date range:', startDate, 'to', endDate);

        // Fetch FI metrics from reporting API
        const metricsUrl = `${api.hostname}/api/v1/reporting/fi-metrics/?terra_fi_id=${fiId}&start_date=${startDate}&end_date=${endDate}`;
        console.log('Fetching FI metrics from:', metricsUrl);
        
        const metricsResponse = await fetch(
          metricsUrl,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token.accessToken}`,
            },
          }
        );
        
        console.log('FI Metrics response status:', metricsResponse.status);
        
        if (!metricsResponse.ok) {
          const errorText = await metricsResponse.text();
          console.error('FI Metrics error response:', errorText);
          console.error('Response headers:', [...metricsResponse.headers.entries()]);
          throw new Error(`Failed to fetch FI metrics: ${metricsResponse.status}`);
        }
        
        const metricsData = await metricsResponse.json();
        console.log('FI Metrics data received:', metricsData);

        // Should get array with single FI data
        // terra_fi_id is a string from backend, so compare as strings
        const fiMetrics = metricsData.find(
          (item) => item.terra_fi_id === String(fiId)
        ) || {
          transaction_count: 0,
          total_volume_usd: 0,
          total_commission_owed: 0,
          total_commission_paid: 0,
        };

        console.log('Filtered FI metrics for ID', fiId, ':', fiMetrics);
        setMetrics(fiMetrics);

        // Fetch merchant list for this FI
        const merchantsUrl = `${api.hostname}/api/v1/reporting/account-metrics/?terra_fi_id=${fiId}&start_date=${startDate}&end_date=${endDate}`;
        console.log('Fetching account metrics from:', merchantsUrl);
        
        const merchantsResponse = await fetch(
          merchantsUrl,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token.accessToken}`,
            },
          }
        );
        
        console.log('Account Metrics response status:', merchantsResponse.status);
        
        if (!merchantsResponse.ok) {
          const errorText = await merchantsResponse.text();
          console.error('Account Metrics error response:', errorText);
          console.error('Response headers:', [...merchantsResponse.headers.entries()]);
          throw new Error(`Failed to fetch merchant metrics: ${merchantsResponse.status}`);
        }
        
        const merchantsData = await merchantsResponse.json();
        console.log('Account Metrics data received:', merchantsData);

        setMerchants(merchantsData || []);
      } catch (err) {
        console.error('Failed to load FI dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    if (fiId && token) {
      fetchDashboardData();
    }
  }, [fiId, token]);

  const handleViewTransactions = (accountId) => {
    // Navigate to SolventPay transactions page with query params for filtering
    navigate(`/solventpay/transactions?fi_id=${fiId}&account_id=${accountId}`);
  };

  const formatCurrency = (value) => {
    if (value == null) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const formatNumber = (value) => {
    if (value == null) return '0';
    return value.toLocaleString();
  };

  if (loading) {
    return <InnerLoader visible={true} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const fiName = financialInstitution?.financial_institution_business_name || 'Financial Institution';

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {fiName} Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last 30 days performance metrics
        </Typography>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                <FontAwesomeIcon icon={['fal', 'receipt']} fixedWidth /> Transactions
              </Typography>
              <Typography variant="h4">
                {formatNumber(metrics?.transaction_count)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                <FontAwesomeIcon icon={['fal', 'dollar-sign']} fixedWidth /> Volume
              </Typography>
              <Typography variant="h4">
                {formatCurrency(metrics?.total_volume_usd)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            onClick={() => setCommissionsModalOpen(true)}
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.2s',
            }}
          >
            <CardContent>
              <Typography color="error" gutterBottom>
                <FontAwesomeIcon icon={['fal', 'coins']} fixedWidth /> Commission Owed
              </Typography>
              <Typography variant="h4" color="error">
                {formatCurrency(metrics?.total_commission_owed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to manage
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card
            onClick={() => setCommissionsModalOpen(true)}
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 4 },
              transition: 'box-shadow 0.2s',
            }}
          >
            <CardContent>
              <Typography color="success.main" gutterBottom>
                <FontAwesomeIcon icon={['fal', 'check-circle']} fixedWidth /> Commission Paid
              </Typography>
              <Typography variant="h4" color="success.main">
                {formatCurrency(metrics?.total_commission_paid)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click to view history
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Merchant List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Merchant Accounts
          </Typography>

          {merchants.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              No merchant accounts found for this FI in the selected date range.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell align="right">Transactions</TableCell>
                    <TableCell align="right">Volume</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merchants.map((merchant) => (
                    <TableRow key={merchant.terra_account_id}>
                      <TableCell>
                        {merchant.account_name || `Account #${merchant.terra_account_id}`}
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(merchant.transaction_count)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(merchant.total_volume_usd)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewTransactions(merchant.terra_account_id)}
                        >
                          View Transactions
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Commission Payout Modal */}
      <CommissionPayoutModal
        open={commissionsModalOpen}
        onClose={() => setCommissionsModalOpen(false)}
        fiId={parseInt(fiId)}
        fiName={fiName}
        onPayoutCreated={() => {
          // Refresh dashboard data after payout creation
          window.location.reload();
        }}
      />
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    financialInstitutions: getFIs(state),
    token: getToken(state),
  };
};

export default connect(mapStateToProps)(FIDashboardPage);
